import React, { useEffect, useState } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Tooltip,
  Button,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  CircularProgress,
  Select,
  MenuItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PageShell from "./PageShell";
import PageContainer from "./components/PageContainer";
import PageHeader from "./components/PageHeader";
import DataCard, { tableHeaderCellSx } from "./components/DataCard";
import apiClient from "./utils/apiClient"; // Add back the apiClient import
import { useRoleStore } from "./store/roleStore"; // Import useRoleStore
import Footer from "./Footer";
import SecurityIcon from "@mui/icons-material/Security";

export default function Role() {
  // Use roleStore instead of local state and direct API calls
  const { roles, isLoading, fetchRoles } = useRoleStore();
  const [deleteRoleId, setDeleteRoleId] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newRole, setNewRole] = useState({
    id: "",
    title: "",
    description: "",
    type: "product"
  });
  const [editingRole, setEditingRole] = useState(null);

  useEffect(() => {
    // Fetch roles only if needed, using the centralized roleStore
    if (roles.length === 0) {
      fetchRoles();
    }
  }, [roles.length, fetchRoles]);

  const handleAddRole = () => {
    setNewRole({ id: "", title: "", description: "", type: "product"}); // Reset new role form
    setIsAddDialogOpen(true);
  };

  const handleCloseAddDialog = () => {
    setNewRole({ id: "", title: "", description: "" }); // Clear form
    setIsAddDialogOpen(false);
  };

  const handleSaveAdd = async () => {
    try {
      await apiClient.post("/role", newRole);
      fetchRoles(); // Refresh roles from the store
      handleCloseAddDialog();
    } catch (error) {
      console.error("Error adding role:", error);
    }
  };

  const handleNewRoleFieldChange = (field, value) => {
    setNewRole((prevRole) => ({
      ...prevRole,
      [field]: value,
    }));
  };

  const handleEditRole = (role) => {
    setEditingRole(role);
    setIsEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditingRole(null);
    setIsEditDialogOpen(false);
  };

  const handleOpenDeleteDialog = (roleId) => {
    setDeleteRoleId(roleId);
    setIsDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteRoleId(null);
    setIsDeleteDialogOpen(false);
  };

  const handleDeleteRole = async () => {
    try {
      await apiClient.delete(`/role/${deleteRoleId}`);
      fetchRoles(); // Refresh roles from the store
      handleCloseDeleteDialog();
    } catch (error) {
      console.error("Error deleting role:", error);
    }
  };

  const handleSaveEdit = async () => {
    try {
      await apiClient.put(`/role/${editingRole.id}`, editingRole);
      fetchRoles(); // Refresh roles from the store
      handleCloseEditDialog();
    } catch (error) {
      console.error("Error updating role:", error);
    }
  };

  const handleEditFieldChange = (field, value) => {
    setEditingRole((prevRole) => ({
      ...prevRole,
      [field]: value,
    }));
  };

  return (
    <PageShell>
      <PageContainer>
        <PageHeader
          icon={<SecurityIcon />}
          title="Roles"
          subtitle="Define product and user roles for access control"
          action={
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddRole}
            >
              Add Role
            </Button>
          }
        />
        <DataCard noPadding>
          {isLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={tableHeaderCellSx}>Id</TableCell>
                  <TableCell sx={tableHeaderCellSx}>Title</TableCell>
                  <TableCell sx={tableHeaderCellSx}>Type</TableCell>
                  <TableCell sx={tableHeaderCellSx}>Description</TableCell>
                  <TableCell sx={tableHeaderCellSx} align="right">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {roles.map((role) => (
                  <TableRow key={role.id} hover>
                    <TableCell>{role.id}</TableCell>
                    <TableCell>{role.title}</TableCell>
                    <TableCell>{role.type}</TableCell>
                    <TableCell>{role.description}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit Role">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEditRole(role)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Role">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDeleteDialog(role.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DataCard>
      </PageContainer>
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="delete-role-dialog-title"
        aria-describedby="delete-role-dialog-description"
      >
        <DialogTitle id="delete-role-dialog-title">
          {"Delete Role?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-role-dialog-description">
            Are you sure you want to delete role with ID {deleteRoleId}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteRole} color="secondary" autoFocus>
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog
        open={isEditDialogOpen}
        onClose={handleCloseEditDialog}
        aria-labelledby="edit-role-dialog-title"
        aria-describedby="edit-role-dialog-description"
      >
        <DialogTitle id="edit-role-dialog-title">Edit Role</DialogTitle>
        <DialogContent>
          <TextField
            label="Id"
            value={editingRole?.id || ""}
            fullWidth
            margin="normal"
            disabled
          />
          <TextField
            label="Title"
            value={editingRole?.title || ""}
            fullWidth
            margin="normal"
            onChange={(e) => handleEditFieldChange("title", e.target.value)}
          />
          <TextField
            label="Description"
            value={editingRole?.description || ""}
            fullWidth
            margin="normal"
            onChange={(e) => handleEditFieldChange("description", e.target.value)}
          />
          <Box sx={{ mt: 2 }}>
            <Select
              label="Type"
              value={editingRole?.type || "product"}
              fullWidth
              onChange={(e) => handleEditFieldChange("type", e.target.value)}
              displayEmpty
            >
              <MenuItem value="product">product</MenuItem>
              <MenuItem value="user">user</MenuItem>
            </Select>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleSaveEdit} color="secondary" autoFocus>
            Save
          </Button>
        </DialogActions>
      </Dialog>
      {/* Add Role Dialog */}
      <Dialog
        open={isAddDialogOpen}
        onClose={handleCloseAddDialog}
        aria-labelledby="add-role-dialog-title"
        aria-describedby="add-role-dialog-description"
      >
        <DialogTitle id="add-role-dialog-title">Add New Role</DialogTitle>
        <DialogContent>
          <TextField
            label="Id"
            value={newRole.id}
            fullWidth
            margin="normal"
            onChange={(e) =>
              handleNewRoleFieldChange("id", e.target.value)
            }
          />
          <TextField
            label="Title"
            value={newRole.title}
            fullWidth
            margin="normal"
            onChange={(e) =>
              handleNewRoleFieldChange("title", e.target.value)
            }
          />
          <TextField
            label="Description"
            value={newRole.description}
            fullWidth
            margin="normal"
            onChange={(e) =>
              handleNewRoleFieldChange("description", e.target.value)
            }
          />
          <Box sx={{ mt: 2 }}>
            <Select
              label="Type"
              value={newRole.type || "product"}
              fullWidth
              onChange={(e) => handleNewRoleFieldChange("type", e.target.value)}
              displayEmpty
            >
              <MenuItem value="product">product</MenuItem>
              <MenuItem value="user">user</MenuItem>
            </Select>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleSaveAdd} color="secondary" autoFocus>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
}
